import React, { FunctionComponent, useState } from 'react';
import classnames from 'classnames';

import { P, Product, ProductOfferTable as ProductTableComponent, SelectOption, Spinner } from '@savebetter/components';
import {
  ProductSortingOptionTypes,
  SortOrder
} from '@savebetter/components/lib/src/components/molecules/ProductOfferTable/ProductOfferTable.types';

import { postLoginUrl } from '../../../config';
import { useBankProducts } from '../context/BankProductsContext';
import { Datasources, useDatasources } from '../context/DataSourceContext';
import {
  defaultProductTypeOptions,
  FeaturedProductType,
  ProductOfferQueryItem,
  ProductOfferTypeQueryItem,
  ProductTableType,
  useProductOfferDocument,
  useProductOffers,
  useProductOfferTypes
} from '../hooks/useProductOffer';
import { getPreparedProducts } from '../utils';
import { buildPdf } from '../utils/buildPdf';

import { PageSectionBlok, PageSectionProps } from './PageSection';
import { PageSectionHeader } from './PageSectionHeader';
import { Section } from './Section';

interface Blok extends PageSectionBlok {
  featuredProducts: FeaturedProductType[];
  bankCode: string;
  productId: string;
  showSelectedProductTypes?: ProductTableType[];
  hasFinancialInstitutionsDropdown?: boolean;
}

interface ProductOfferTableProps extends PageSectionProps {
  blok: Blok;
  isIframe?: boolean;
}

interface Option {
  label: string;
  value: string;
}

interface GenerateProductTypeOptionsProps {
  productsToPrepare: Array<ProductOfferQueryItem>;
  allProductOfferTypes: Array<ProductOfferTypeQueryItem>;
  productId: string;
  productBankId: string;
  showSelectedProductTypes: ProductTableType[];
  hasFeaturedProducts: boolean;
}
const generateProductTypeOptions = ({
  productsToPrepare: products,
  allProductOfferTypes,
  productId,
  productBankId,
  showSelectedProductTypes,
  hasFeaturedProducts
}: GenerateProductTypeOptionsProps): Array<SelectOption<ProductTableType>> => {
  if (productId && products.length > 1) {
    // choose the type from the single product on Product Page
    return defaultProductTypeOptions.filter(productType => productType.value === products[0].type);
  } else if (productBankId) {
    // get the Offer types that exists for that bank
    const uniqueProductTypes: Set<string> = new Set(allProductOfferTypes.map(productType => productType.type));
    return defaultProductTypeOptions.filter(option => uniqueProductTypes.has(option.value));
  } else if (showSelectedProductTypes.length) {
    // get only the offer that are selected by marketing team,
    // add Feature Products tab,if the add feature products for any if those product type.
    showSelectedProductTypes = hasFeaturedProducts
      ? ['FEATURED_PRODUCTS', ...showSelectedProductTypes]
      : showSelectedProductTypes;
    return defaultProductTypeOptions.filter(productType => showSelectedProductTypes.includes(productType.value));
  }
  // the basic product Table
  return hasFeaturedProducts
    ? defaultProductTypeOptions
    : defaultProductTypeOptions.filter(option => option.value !== 'FEATURED_PRODUCTS');
};

// move to some other files
const sortProducts = (products: Product[], sortBy: ProductSortingOptionTypes, sortOrder: SortOrder) => {
  if (sortBy === 'annualPercentageYield') {
    return [...products].sort((a, b) => {
      const aApy = parseFloat(a.annualPercentageYield);
      const bApy = parseFloat(b.annualPercentageYield);

      if (aApy === bApy) {
        //if apy are same then sort based on names.
        return b.bankName.localeCompare(a.bankName);
      }
      // Sort based on the apy
      return sortOrder === 'DESC' ? bApy - aApy : aApy - bApy;
    });
  }
  if (sortBy === 'maturity' && products[0].maturity) {
    return [...products].sort((a, b) => {
      const monthA = parseFloat(a.maturity!.replace(/%/, ''));
      const monthB = parseFloat(b.maturity!.replace(/ months/, ''));
      return sortOrder === 'DESC' ? monthB - monthA : monthA - monthB;
    });
  }
  if (sortBy === 'productName') {
    return [...products].sort((a, b) =>
      sortOrder === 'DESC' ? b.productName.localeCompare(a.productName) : a.productName.localeCompare(b.productName)
    );
  }
  if (sortBy === 'bankName') {
    if (sortOrder === 'DESC') {
      return [...products].sort((a, b) => b.bankName.localeCompare(a.bankName));
    }
    if (sortOrder === 'ASC') {
      return [...products].sort((a, b) => a.bankName.localeCompare(b.bankName));
    }
  }
  return products;
};

const getParsedValue = (value: string) => {
  // Try/catch for JSON.parse errors
  try {
    return JSON.parse(value);
  } catch (ex) {
    return [];
  }
};

const buildFinancialInstitutionsContext = (datasources: Datasources | null) =>
  datasources?.['financial_institutions'].edges.reduce(
    (allInstitutions, { node }) => ({
      ...allInstitutions,
      [node.name]: getParsedValue(node.value)
    }),
    {}
  ) ?? {};

const getInActiveProductsIds = (datasources: Datasources | null) => {
  try {
    return datasources?.['inactive_products'].edges.map(product => product.node.value) || [];
  } catch (ex) {
    return [];
  }
};

const getBankLogosLink = (datasources: Datasources | null) =>
  datasources?.['bank_logo_links']?.edges.reduce(
    (allLogosMap, { node }) => ({
      ...allLogosMap,
      [node.name]: node.value
    }),
    {}
  ) ?? {};

const filterFinancialInstitutions = (
  products: Product[],
  financialInstitutionsContext: Record<string, Array<string>>,
  selectedFinancialInstitutions: Array<Option>
) => {
  // No filtering if nothing is selected
  if (selectedFinancialInstitutions.length < 1) {
    return products;
  }

  return products.filter(product => {
    const sourceInstitutions = financialInstitutionsContext[product.bankId] || [];
    const selectedInstitutions = selectedFinancialInstitutions.map(selected => selected.value);

    return sourceInstitutions.some(sourceInstitution => selectedInstitutions.includes(sourceInstitution));
  });
};

export const ProductOfferTable: FunctionComponent<ProductOfferTableProps> = ({ blok, sectionClassName, isIframe }) => {
  const {
    title,
    bankCode,
    featuredProducts = [],
    productId,
    hasPaddingTop,
    hasPaddingBottom = false,
    hasBackground = false,
    hasBorder = false,
    showSelectedProductTypes = [],
    hasFinancialInstitutionsDropdown = false
  } = blok;
  const productBankId = bankCode; // need to rename it in storyblok
  // default values
  const pageSize = 5;
  const defaultDepositAmount = 50000;

  // state Variables
  const [currentProductType, setCurrentProductType] = useState<ProductTableType>('FEATURED_PRODUCTS');
  const [sortBy, setSortBy] = useState<ProductSortingOptionTypes>('annualPercentageYield');
  const [sortOrder, setSortOrder] = useState<SortOrder>('DESC');
  const [depositAmount, setDepositAmount] = useState(defaultDepositAmount);
  const [pageNumber, setPageNumber] = useState(1);
  const [financialInstitutions, setFinancialInstitutions] = useState<Option[]>([]);

  // api calls
  const { data: productOfferTypesData } = useProductOfferTypes(productBankId);
  const { data: productOfferData } = useProductOffers({ currentProductType, productBankId });
  const [getProductOfferDocument] = useProductOfferDocument();

  const { contextProducts, setContextProducts } = useBankProducts();

  const { contextDatasources: datasources } = useDatasources();

  const financialInstitutionsContext = buildFinancialInstitutionsContext(datasources);
  const bankLogosLinks = getBankLogosLink(datasources);

  if (!productOfferTypesData || !productOfferData?.productOffers) {
    return (
      <div className="flex justify-center pb-12">
        <Spinner />
      </div>
    );
  }

  // prepare products
  const allProducts = productOfferData?.productOffers.nodes;

  let productsToPrepare = productId ? allProducts.filter(product => product.id === productId) : allProducts;

  const inActiveProductIds = getInActiveProductsIds(datasources);
  productsToPrepare = [...productsToPrepare].filter(product => !inActiveProductIds.includes(product.id));

  let preparedProducts = getPreparedProducts([...productsToPrepare], depositAmount);

  // setting the Product context so that custom vars the page can be resolved.
  !contextProducts && setContextProducts(preparedProducts);

  // prepare tabs
  const allProductOfferTypes = productOfferTypesData.productOffers.nodes;
  const productTypeOptions =
    allProductOfferTypes &&
    generateProductTypeOptions({
      productsToPrepare,
      allProductOfferTypes,
      productId,
      productBankId,
      showSelectedProductTypes,
      hasFeaturedProducts: !!featuredProducts.length
    });

  if (productId || productBankId || showSelectedProductTypes) {
    if (currentProductType === 'FEATURED_PRODUCTS' && !featuredProducts.length) {
      setCurrentProductType(productTypeOptions[0]?.value);
    }
    if (showSelectedProductTypes.length === 1 && currentProductType !== showSelectedProductTypes[0]) {
      setCurrentProductType(showSelectedProductTypes[0]);
    }
  }

  if (currentProductType === 'FEATURED_PRODUCTS' && featuredProducts.length) {
    const productsMap = new Map(preparedProducts.map(product => [product.id, product]));

    preparedProducts = featuredProducts.reduce((acc, current) => {
      const featuredProduct = productsMap.get(current.productId);

      if (featuredProduct) {
        return [...acc, featuredProduct];
      }

      return acc;
    }, [] as Array<Product>);
  }

  if (currentProductType !== 'FEATURED_PRODUCTS') {
    preparedProducts = sortProducts(preparedProducts, sortBy, sortOrder);
  }

  preparedProducts = filterFinancialInstitutions(preparedProducts, financialInstitutionsContext, financialInstitutions);

  const productsToShow = [...preparedProducts].splice(0, pageNumber * pageSize);

  // handlers
  const handleProductTypeChange = (value: ProductTableType) => {
    setCurrentProductType(value);
  };

  const removeFinancialInstitution = (value: string) => {
    const filteredInstitutions = financialInstitutions.filter(elem => elem.value !== value);
    setFinancialInstitutions(filteredInstitutions);
  };

  const openProductTerms = async (productId: string): Promise<string | void> => {
    const { data, error } = await getProductOfferDocument({ variables: { productId } });
    const base64Content = data?.productOfferDocument?.content;
    if (error) {
      throw error;
    }
    if (!base64Content) {
      throw new Error('Product offer document is empty or null');
    }

    return buildPdf(base64Content);
  };

  return (
    <Section
      width="md"
      hasBackground={hasBackground}
      hasBorder={hasBorder}
      className={classnames(sectionClassName, 'py-0')}
      hasPaddingBottom={hasPaddingBottom}
      hasPaddingTop={hasPaddingTop}
    >
      {title && <PageSectionHeader>{title}</PageSectionHeader>}
      <ProductTableComponent
        products={productsToShow}
        isIframe={isIframe}
        openProductTerms={openProductTerms}
        onInvest={(product): void => {
          const url = `${postLoginUrl}/auth/register?productId=${product.id}`;

          if (isIframe) {
            window.open(url, '_blank');
            return;
          }

          window.location.href = url;
        }}
        onLoadMore={() => {
          setPageNumber(pageNumber + 1);
        }}
        hideLoadMoreButton={productsToShow.length === preparedProducts.length}
        currentProductType={currentProductType}
        productTypeOptions={productTypeOptions}
        onProductTypeChange={(value): void => {
          handleProductTypeChange(value as ProductTableType);
          setSortBy('annualPercentageYield');
          setPageNumber(1);
        }}
        handleSortBy={(newSortBy: ProductSortingOptionTypes) => {
          if (newSortBy === sortBy) {
            sortOrder === 'ASC' ? setSortOrder('DESC') : setSortOrder('ASC');
          } else {
            setSortBy(newSortBy);
            setSortOrder('DESC');
          }
        }}
        handleDepositAmount={(amount: number) => {
          setDepositAmount(amount);
        }}
        currentSortBy={sortBy}
        currentSortOrder={sortOrder}
        emptyListContent={
          <div className="text-center">
            <P>Sorry, no products of this type currently available.</P>
          </div>
        }
        financialInstitutionsContainerOptions={financialInstitutions}
        financialInstitutionsDropdownOptions={financialInstitutions}
        removeFinancialInstitution={removeFinancialInstitution}
        handleFinancialInstitutionChange={setFinancialInstitutions}
        financialInstitutionsContext={financialInstitutionsContext}
        hasFinancialInstitutionsDropdown={hasFinancialInstitutionsDropdown}
        defaultDepositAmount={defaultDepositAmount}
        bankLogosLinksContext={bankLogosLinks}
      />
    </Section>
  );
};
